import { createContext, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/** Умолчание паузы для поддерева. null — общесайтовое (DEFAULT_DELAY). */
const DelayContext = createContext<number | null>(null)

/**
 * Пауза подсказок для целого раздела.
 *
 * Раздел, где подсказки объясняют устройство экрана, а не дублируют подпись
 * («Умные помощники»: скрепка, шапка документа, масштаб листа), ждать полсекунды
 * не должен — человек ведёт мышь к незнакомой кнопке за объяснением
 * (просьба владельца 13.09). Умолчание поддерева вместо delay={0} на каждом
 * вызове: иначе следующая подсказка в разделе снова приедет с паузой.
 * Явный delay у конкретной подсказки всё равно сильнее.
 */
export function TooltipDelay({ ms, children }: { ms: number; children: React.ReactNode }) {
	return <DelayContext.Provider value={ms}>{children}</DelayContext.Provider>
}

interface SideTooltipProps {
	/** Текст тултипа. Если не передан — просто рендерит children */
	text?: string
	children: React.ReactNode
	/** Задержка перед показом, мс. Не передана — умолчание раздела (TooltipDelay),
	 *  иначе общесайтовые 450. */
	delay?: number
	/** Максимальная ширина, px */
	maxWidth?: number
	/** Предпочтительная сторона. По умолчанию 'auto' — выбирает куда влезает */
	preferSide?: 'right' | 'left' | 'auto'
	/** CSS-класс для обёртки */
	className?: string
	/** Inline-стиль на триггер-обёртку (напр. flexGrow для сегментов таймлайна) */
	style?: React.CSSProperties
}

interface TipPos {
	y: number              // центр триггера по вертикали (viewport)
	x: number              // правый или левый край триггера (viewport)
	side: 'right' | 'left'
}

interface RenderState {
	top: number            // финальная top-координата тултипа
	arrowTop: number       // top-координата стрелки внутри тултипа (px)
	/** Горизонтальный кламп по ФАКТИЧЕСКОЙ ширине (аудит 27.08: у правого
	 * края вьюпорта тултип уезжал за экран и текст резался — эвристика
	 * стороны оценивала место по константе, а не по реальной ширине). */
	shiftX: number
	visible: boolean
}

const DEFAULT_DELAY = 450
const TIP_GAP = 8
const TIP_ESTIMATE_W = 240
const VIEWPORT_MARGIN = 8

export default function SideTooltip({
	text,
	children,
	delay,
	maxWidth = 220,
	preferSide = 'auto',
	className,
	style,
}: SideTooltipProps) {
	const ctxDelay = useContext(DelayContext)
	const showDelay = delay ?? ctxDelay ?? DEFAULT_DELAY
	const [tip, setTip] = useState<TipPos | null>(null)
	const [render, setRender] = useState<RenderState>({ top: 0, arrowTop: 0, shiftX: 0, visible: false })
	const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
	const triggerRef = useRef<HTMLSpanElement>(null)
	const tipDivRef = useRef<HTMLDivElement>(null)

	// Чистим таймер при unmount
	useEffect(() => () => { if (timer.current) clearTimeout(timer.current) }, [])

	// После рендера тултипа — измеряем высоту, зажимаем позицию в viewport, корректируем стрелку
	useLayoutEffect(() => {
		if (!tip || !tipDivRef.current) return
		const h = tipDivRef.current.offsetHeight
		const centered = tip.y - h / 2
		const clamped = Math.max(
			VIEWPORT_MARGIN,
			Math.min(centered, window.innerHeight - h - VIEWPORT_MARGIN),
		)
		const arrowTop = tip.y - clamped
		// Горизонтальный кламп по фактической ширине: правый край не дальше
		// (innerWidth − margin), левый — не левее margin. Сдвиг аддитивен и
		// в обычных случаях равен нулю — поведение 19 существующих мест
		// не меняется.
		const w = tipDivRef.current.offsetWidth
		let shiftX = 0
		if (tip.side === 'right') {
			const overflow = tip.x + w - (window.innerWidth - VIEWPORT_MARGIN)
			if (overflow > 0) shiftX = -overflow
			if (tip.x + shiftX < VIEWPORT_MARGIN) shiftX = VIEWPORT_MARGIN - tip.x
		} else {
			const left = tip.x - w
			if (left < VIEWPORT_MARGIN) shiftX = VIEWPORT_MARGIN - left
			if (tip.x + shiftX > window.innerWidth - VIEWPORT_MARGIN)
				shiftX = window.innerWidth - VIEWPORT_MARGIN - tip.x
		}
		setRender({ top: clamped, arrowTop, shiftX, visible: true })
	}, [tip])

	function show() {
		if (!text || !triggerRef.current) return
		if (timer.current) clearTimeout(timer.current)

		const doShow = () => {
			if (!triggerRef.current) return
			const rect = triggerRef.current.getBoundingClientRect()
			const side: 'right' | 'left' =
				preferSide !== 'auto'
					? preferSide
					: rect.right + TIP_ESTIMATE_W + TIP_GAP <= window.innerWidth
					? 'right'
					: 'left'

			setTip({
				y: rect.top + rect.height / 2,
				x: side === 'right' ? rect.right + TIP_GAP : rect.left - TIP_GAP,
				side,
			})
		}

		if (showDelay === 0) {
			doShow()
		} else {
			timer.current = setTimeout(doShow, showDelay)
		}
	}

	function hide() {
		if (timer.current) clearTimeout(timer.current)
		timer.current = null
		setTip(null)
	}

	if (!text) return <>{children}</>

	return (
		<>
			<span
				ref={triggerRef}
				className={`side-tooltip-trigger${className ? ` ${className}` : ''}`}
				style={style}
				onMouseEnter={show}
				onMouseLeave={hide}
				onFocus={show}
				onBlur={hide}
			>
				{children}
			</span>
			{tip && createPortal(
				<div
					ref={tipDivRef}
					className={`side-tooltip side-tooltip--${tip.side}`}
					style={{
						position: 'fixed',
						top: render.top,
						...(tip.side === 'right'
							? { left: tip.x + render.shiftX }
							: { right: window.innerWidth - tip.x - render.shiftX }),
						maxWidth,
						zIndex: 10000,
						visibility: render.visible ? 'visible' : 'hidden',
					}}
				>
					{text}
					<div className="side-tooltip__arrow" style={{ top: render.arrowTop || undefined }} />
				</div>,
				document.body,
			)}
		</>
	)
}
